import firebase from '../../../../firebase/compat';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { v4 as uuid } from 'uuid';
import describeQueryError from "../../../../helpers/firestore/describeQueryError";
import html2canvas from "html2canvas";

import { db } from "../../../../firebase/config";
import { client } from "../../../../hooks/Client";
import Location from "../../../../hooks/Location";
import { isSuperAdminTenant } from "../../../../utils/superAdminTenant";

import { BaseWidget, Container } from "../widgets";
import { deserializeWidget, serializeWidget } from "../utils/serializer";
import buildReportCopy from "../utils/buildReportCopy";
import isReportLocked from "../../../../helpers/liveReportHelpers/isReportLocked";
import { widgetsNeedingSave } from "../utils/widgetsNeedingSave";
import computeThumbnailScale from "../utils/computeThumbnailScale";
import softDeleteLiveReport, { isEditorRemovedWidget } from "../utils/softDeleteLiveReport";

import { SavedIcon } from "../../../../StateManagment/SavedIcon";
import { Auth } from "../../../../StateManagment/Auth";

/**
 * @typedef {import('../../../../types/LiveReports2').LiveReports2Doc} LiveReports2Doc
 *
 * @typedef {object} LiveReportCrudContext
 * @property {string} reportId The ID of the report being edited (used for Firebase syncing)
 * @property {LiveReports2Doc} [report] The full report document from Firestore
 * @property {'loading'|'found'|'missing'|'error'} reportLookup Where the lookup of `report` stands. `report` alone can't say: it stays undefined both while loading and when nothing exists, so a page would claim 'wordt geladen' forever. AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 * @property {(isTemplate?: boolean, name?: string) => Promise<LiveReports2Doc>} createNewReport Function to create a new report (optionally as a template). `name` defaults to 'Live rapportage <datum>'; the '+' in the list passes the title typed in NewReportModal.
 * @property {(source: LiveReports2Doc, isTemplate?: boolean, name?: string) => Promise<LiveReports2Doc>} createNewReportFromSource Function to create a new report by copying widgets from an existing source report (template or own). `name` defaults to 'Live rapportage <datum>'; copying passes `copyReportName(source)`.
 * @property {(report: LiveReports2Doc, updates: Partial<LiveReports2Doc>) => Promise<void>} updateReport Function to update report fields
 * @property {(report: LiveReports2Doc, onComplete?: () => void) => Promise<void>} deleteReport Function to (soft) delete given current report
 * @property {(report: LiveReports2Doc, widgets: BaseWidget[]) => Promise<void>} saveWidgets Function to save the current widgets to Firestore and update the report thumbnail
 * @property {(reportId: string) => Promise<void>} cleanupDeletedWidgets Function to permanently delete widgets that were marked as deleted in the editor.
 * @property {(reportId: string) => Promise<void>} generateShareCode Generate or rotate the 6-digit share code on a report (Cloud Function).
 * @property {(report: LiveReports2Doc) => Promise<void>} clearShareCode Remove the share code from a report — opens it back up to anyone with the link.
 */

/**
 * @type {React.Context<LiveReportCrudContext|null>}
 */
export const LiveReportCrudContext = createContext(/** @type {LiveReportCrudContext|null} */ (null))

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export const LiveReportCrudContextProvider = ({
    children
}) => {

    // Hooks
    const reportId = Location()[3];

    const [saved, setSaved] = useContext(SavedIcon);
    const [auth] = useContext(Auth);

    /** @type {ReturnType<typeof useState<LiveReports2Doc>>} */
    const [report, setReport] = useState();
    /** @type {ReturnType<typeof useState<'loading'|'found'|'missing'|'error'>>} */
    const [reportLookup, setReportLookup] = useState(/** @type {'loading'|'found'|'missing'|'error'} */ ('loading'));

    useEffect(() => {
        if (!reportId || reportId === '') {
            return;
        }

        cleanupDeletedWidgets(reportId);
    }, [reportId]);

    useEffect(() => {
        if (!reportId || reportId === '') {
            return;
        }

        // Firestore rules require CompagnyID match (own) or IsTemplate=true (cross-tenant template).
        // Subscribe to own first; only spin up the template fallback if own returns empty.
        let unsubTemplate = null;

        const unsubOwn = db
            .collection("LiveReports2")
            .where("ID", "==", reportId)
            .where("CompagnyID", "==", client)
            .limit(1)
            .onSnapshot((snapshot) => {
                const doc = snapshot.docs[0];
                if (doc) {
                    // @ts-expect-error see setReport type below
                    setReport({ ...doc.data(), docid: doc.id });
                    setReportLookup('found');
                    if (unsubTemplate) {
                        unsubTemplate();
                        unsubTemplate = null;
                    }
                    return;
                }
                if (!unsubTemplate) {
                    unsubTemplate = db
                        .collection("LiveReports2")
                        .where("ID", "==", reportId)
                        .where("IsTemplate", "==", true)
                        .limit(1)
                        .onSnapshot((snap) => {
                            const tpl = snap.docs[0];
                            if (tpl) {
                                // @ts-expect-error see setReport type below
                                setReport({ ...tpl.data(), docid: tpl.id });
                                setReportLookup('found');
                            } else {
                                // Niet van deze organisatie en geen template: er komt niets.
                                setReportLookup('missing');
                            }
                        }, (error) => {
                            console.error(
                                describeQueryError('LiveReports2', [['ID', '==', reportId], ['IsTemplate', '==', true]], error),
                                error
                            );
                            setReportLookup('error');
                        });
                }
            }, (error) => {
                console.error(
                    describeQueryError('LiveReports2', [['ID', '==', reportId], ['CompagnyID', '==', client]], error),
                    error
                );
                setReportLookup('error');
            });

        return () => {
            unsubOwn();
            if (unsubTemplate) unsubTemplate();
        };
    }, [reportId]);

    /**
     * @type {LiveReportCrudContext['createNewReport']}
     */
    const createNewReport = useCallback(async (isTemplate = false, name = undefined) => {

        if (isTemplate && !isSuperAdminTenant(client)) {
            throw new Error('Templates can only be created from the Deccos tenant.');
        }

        const existingSnap = await db.collection("LiveReports2")
            .where("CompagnyID", "==", client)
            .get();

        // Fresh, not the module-level timestamp: that is the page-load moment, so
        // every report made in one session got the same CreatedAt and the list
        // (newest first) could not tell them apart.
        // AI-CONTEXT: See docs/pitfalls.md#state-stale-module-level-timestamp
        const createdAt = firebase.firestore.Timestamp.fromDate(new Date());

        /** @type {LiveReports2Doc} */
        const newReport = {
            ID: uuid(),
            CompagnyID: client,
            IsTemplate: isTemplate,
            Name: name || `Live rapportage ${new Date().toLocaleString()}`,
            Position: existingSnap.size + 1,
            Timestamp: createdAt,
            CreatedAt: createdAt,
            UpdatedAt: createdAt,
            Status: 'draft',
            // Thumbnail: base64
        };

        return db.collection("LiveReports2")
            .add(newReport)
            .then((docRef) => {

                const rootContainer = new Container({}, true);
                rootContainer.id = uuid();

                db.collection('LiveReportWidgets')
                    .add(serializeWidget(newReport.ID, rootContainer, isTemplate));
                    
                return { ...newReport, docid: docRef.id };
            })
            .catch((error) => {
                console.error("Error creating report:", error);
                alert(
                    "Er is een fout opgetreden bij het maken van het rapport. Zie de console voor details."
                );
                throw error;
            });
    }, []);

    /**
     * @type {LiveReportCrudContext['createNewReportFromSource']}
     */
    const createNewReportFromSource = useCallback(async (source, isTemplate = false, name = undefined) => {

        if (isTemplate && !isSuperAdminTenant(client)) {
            throw new Error('Templates can only be created from the Deccos tenant.');
        }

        // Read source widgets. Use the branch that Firestore rules allow:
        // - source is a template → IsTemplate==true (cross-tenant readable)
        // - source is own report → CompagnyID==client
        const baseQuery = db.collection('LiveReportWidgets')
            .where('ReportId', '==', source.ID);

        const widgetSnap = source.IsTemplate
            ? await baseQuery.where('IsTemplate', '==', true).get()
            : await baseQuery.where('CompagnyID', '==', client).get();

        const existingSnap = await db.collection("LiveReports2")
            .where("CompagnyID", "==", client)
            .get();

        const { report: newReport, widgets } = buildReportCopy(
            source,
            widgetSnap.docs.map(doc => ({ ...doc.data(), docid: doc.id })),
            {
                client,
                isTemplate,
                newId: uuid(),
                name: name || `Live rapportage ${new Date().toLocaleString()}`,
                position: existingSnap.size + 1,
                // Fresh for the same reason as in createNewReport.
                timestamp: firebase.firestore.Timestamp.fromDate(new Date()),
            }
        );

        if (widgets.length === 0) {
            // Source had no widgets — fall back to a blank root so the editor opens to something.
            const rootContainer = new Container({}, true);
            rootContainer.id = uuid();
            widgets.push(serializeWidget(newReport.ID, rootContainer, isTemplate));
        }

        /* Report and widgets in ONE batch: a failed write leaves nothing
           behind, instead of a report without its widgets.
           AI-CONTEXT: See docs/pitfalls.md#state-multi-step-signup-leaves-half-an-account */
        const batch = db.batch();
        // A locked copy lives at docid === ID: its widgets only carry the
        // report's ID (ReportId), and the Firestore rule that keeps them
        // locked has to get() the report by that ID.
        // AI-CONTEXT: See docs/features/meetstandaardrapportage.md (T1c)
        const reportDocRef = newReport.Locked
            ? db.collection("LiveReports2").doc(newReport.ID)
            : db.collection("LiveReports2").doc();
        batch.set(reportDocRef, newReport);
        widgets.forEach(data => {
            batch.set(db.collection('LiveReportWidgets').doc(), data);
        });
        await batch.commit();

        return { ...newReport, docid: reportDocRef.id };
    }, []);

    /**
     * @type {LiveReportCrudContext['updateReport']}
     */
    const updateReport = useCallback(async (report, updates) => {
        // Awaited so a failed write rejects for the caller (the persona
        // selector shows it) instead of vanishing as an unhandled rejection.
        await db.collection('LiveReports2')
            .doc(report.docid)
            .update({
                ...updates,
                // Use a fresh Date so UpdatedAt is current, not the module-level timestamp captured at page load.
                UpdatedAt: firebase.firestore.Timestamp.fromDate(new Date())
            });
    }, []);

    const createPreview = useCallback(async (canvasSelector = '.report-builder-canvas') => {

        /** @type {HTMLElement | null} */
        const canvasElement = document.querySelector(canvasSelector);

        if (!canvasElement) {
            throw new Error(`Canvas element not found for selector: ${canvasSelector}`);
        }

        /* Capture downscaled + JPEG-compressed: the thumbnail only renders
           ~100px wide in the reports list, and a full-res PNG of a photo-heavy
           report exceeds Firestore's ~1MB field limit. Explicit white
           background because JPEG has no alpha channel. */
        const canvas = await html2canvas(canvasElement, {
            scale: computeThumbnailScale(canvasElement.offsetWidth),
            backgroundColor: '#ffffff',
        });

        return canvas.toDataURL('image/jpeg', 0.7);
    }, []);

    /**
     * @type {LiveReportCrudContext['cleanupDeletedWidgets']}
     * @todo also delete child widgets within a deleted container. 
     */
    const cleanupDeletedWidgets = useCallback(async (reportId) => {
        const batch = db.batch();

        // Cleanup is for widgets owned by the current tenant only — rules require CompagnyID filter for non-template reads.
        const widgetsSnapshot = await db.collection('LiveReportWidgets')
            .where('ReportId', '==', reportId)
            .where('CompagnyID', '==', client)
            .get();

        widgetsSnapshot.docs.forEach(doc => {
            const widget = /** @type {ReturnType<typeof serializeWidget>} */(
                doc.data()
            );
            // Only widgets removed in the editor — not the ones that went to
            // the trash with their report (those carry DeletedAt).
            if (isEditorRemovedWidget(widget)) {
                batch.delete(doc.ref);
            }
        });

        await batch.commit();
    }, []);

    /**
     * @type {LiveReportCrudContext['saveWidgets']}
     */
    const saveWidgets = useCallback(async (report, widgets) => {

        if (!report || !widgets || widgets.length === 0) {
            alert('Report or widgets not found. Cannot save widgets.');
            return;
        }

        // The editor does not open a locked report for editing (T1b); this
        // catches any path that still tries, before the rule refuses it.
        if (isReportLocked(report)) {
            alert('Deze rapportage is vergrendeld. De opbouw kan niet worden gewijzigd.');
            return;
        }

        /* Only write widgets that changed: new (no docid), dirty (settings or
           parent/order edited) or locally soft-deleted. A clean report state
           skips the whole save — including the html2canvas thumbnail. */
        const changedWidgets = widgetsNeedingSave(widgets);

        if (changedWidgets.length === 0) {
            return;
        }

        setSaved('flex');

        try {
            /* Store preview image */
            const base64Preview = await createPreview();

            updateReport(report, {
                Thumbnail: base64Preview
            });

            /* Store widgets */
            const batch = db.batch();

            const isTemplate = report.IsTemplate ?? false;

            changedWidgets.forEach(widget => {

                const widgetData = serializeWidget(reportId, widget, isTemplate);

                /* Clear the dirty flag at serialize time (restored in the catch
                   below if the commit fails). Edits made while the commit is in
                   flight re-set the flag and survive for the next save. */
                widget.isDirty = false;

                if (widget.docid) {

                    /* Existing widget -> update existing document */
                    const docRef = db
                        .collection('LiveReportWidgets')
                        .doc(widget.docid);

                    batch.update(docRef, widgetData);

                } else {

                    /* New widget -> create a document keyed by the widget's own
                       ID so the write is idempotent. save() is fire-and-forget
                       and docid used to be assigned only AFTER commit, so a
                       second save of the same not-yet-persisted widget
                       (overlapping saves, or an undo/redo replay of a
                       docid:null history snapshot) minted a second doc with a
                       duplicate widget ID. Keying the doc on widget.id makes the
                       repeat write hit the same doc, and assigning docid now
                       (not post-commit) closes the race window.
                       AI-CONTEXT: See docs/pitfalls.md#ui-livereport-viewer-duplicate-widget-id */
                    const docRef = db
                        .collection('LiveReportWidgets')
                        .doc(widget.id);

                    batch.set(docRef, widgetData);

                    widget.docid = docRef.id;
                }
            });

            await batch.commit();
        } catch (error) {
            /* The write didn't land — re-mark everything we tried to save so
               the next save attempt picks these widgets up again. */
            changedWidgets.forEach(widget => {
                widget.isDirty = true;
            });
            console.error('Error saving widgets:', error);
            alert(
                'Er is een fout opgetreden bij het opslaan. ' +
                'Zie de console voor details.'
            );
            throw error;
        } finally {
            setSaved('none');
        }
    }, [reportId, setSaved, updateReport, createPreview]);

    /**
     * @type {LiveReportCrudContext['deleteReport']}
     */
    const deleteReport = useCallback(async (report, onComplete) => {

        /* Soft delete report + widgets together, so restoring the report from
           the trash brings its widgets back (ADR-008). */
        await softDeleteLiveReport({ report, authId: auth?.ID });

        onComplete && onComplete();
    }, [auth?.ID]);

    /**
     * @type {LiveReportCrudContext['generateShareCode']}
     */
    const generateShareCode = useCallback(async (reportId) => {
        const fn = firebase.app().functions('europe-west1').httpsCallable('generateReportShareCode');
        await fn({ reportId });
    }, []);

    /**
     * @type {LiveReportCrudContext['clearShareCode']}
     */
    const clearShareCode = useCallback(async (report) => {
        await db.collection('LiveReports2').doc(report.docid).update({
            ShareCode: firebase.firestore.FieldValue.delete(),
        });
    }, []);

    const value = useMemo(() => ({
        reportId, report, reportLookup,
        createNewReport, createNewReportFromSource, updateReport, deleteReport,
        saveWidgets, cleanupDeletedWidgets,
        generateShareCode, clearShareCode,
    }), [reportId, report, reportLookup, createNewReport, createNewReportFromSource, updateReport, deleteReport, saveWidgets, cleanupDeletedWidgets, generateShareCode, clearShareCode]);

    return (
        <LiveReportCrudContext.Provider value={value}>
            {children}
        </LiveReportCrudContext.Provider>
    )
}
